/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from 'helpers/ajax'
import { createAction } from 'helpers/createAction'
import { ThunkAction } from 'model/store/thunk'
import { SessionData } from 'types/responses/SessionResponse'
import { consoleLog } from 'utils/utility'
import { PeriodType } from 'types/entities/PeriodType'
import i18next from 'i18next'
import { Notification } from 'types/entities/Notification'
import { ReducerSessionPushNotificationAction } from 'store/session/SessionReducer'
import { Driver } from 'types/entities/Driver'
import { SESSION_RESET } from './SessionActionTypes'
import {
    setNotifications,
    pushNotification,
    setSession,
    setProfile,
    setTickets,
    setResult,
    setResults,
    setRanking,
    setMyStats,
    setLanguage,
    setThemeMode,
    setCheckin,
    reset as resetSession,
} from 'store/session/SessionReducer'

export interface RegistrationData {
    name: string
    surname: string
    birthdate: string
    email: string
    experience: string
    username: string
}

class SessionAction {
    static readonly SET_NOTIFICATIONS = 'SessionAction.SetNotifications'
    static readonly PUSH_NOTIFICATION = 'SessionAction.PushNotification'
    static readonly SET_SESSION = 'SessionAction.SetSession'
    static readonly SET_PROFILE = 'SessionAction.SetProfile'
    static readonly SET_TICKETS = 'SessionAction.SetTickets'
    static readonly SET_RESULT = 'SessionAction.SetResult'
    static readonly SET_RESULTS = 'SessionAction.SetResults'
    static readonly SET_RANKING = 'SessionAction.SetRanking'
    static readonly SET_MYSTATS = 'SessionAction.SetMyStats'
    static readonly SET_CHECKIN = 'SessionAction.SetCheckin'
    static readonly SET_LANGUAGE = 'SessionAction.SetLanguage'
    static readonly SET_THEME_MODE = 'SessionAction.SetThemeMode'

    static readonly RESET = SESSION_RESET

    static register = (
        data: RegistrationData
    ): ThunkAction<Promise<any>, any> => {
        return (dispatch): Promise<any> => {
            return ajax.post('/user/register', data).then((r) => {
                // dispatch(SessionAction.setSession(r));
                // history.push("/profile");
                return r.user
            })
        }
    }

    static login = (data: {
        username: string
        password: string
    }): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax.post('/user/login', data).then((r) => {
                dispatch(setSession(r))
                return r
            })
        }
    }

    static loginVisitor = (): ThunkAction<any, any> => {
        return (dispatch, getState) => {
            if (!getState().session?.access_token) {
                return ajax.post('/user/login-visitor').then((r) => {
                    dispatch(setSession(r))
                    return r
                })
            }
        }
    }

    static logout = (): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax
                .post('/user/logout', {
                    close: false,
                    lights: false,
                })
                .finally(() => {
                    // Always clear session state even when logout API fails.
                    dispatch(SessionAction.resetAppState())
                })
        }
    }

    static resetPassword = (
        email: string,
        access_token: string,
        new_password: string,
        app_id?: string
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax
                .post('/user/reset-password', {
                    email: email,
                    password_hash: access_token,
                    new_password: new_password,
                    app_id: app_id,
                })
                .then((r) => {
                    // dispatch(SessionAction.setSession(r));
                    return r
                })
        }
    }

    static forgottenPassword = (
        email: string,
        app_url?: string
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch): Promise<any> => {
            return ajax
                .post('/user/forgotten-password', {
                    email: email,
                    app_url: app_url,
                })
                .then((r) => {
                    // dispatch(SessionAction.setSession(r));
                    return r
                })
        }
    }

    static fetchProfile = (): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax
                .get('/user/profile', {})
                .then((r) => {
                    dispatch(setProfile(r))
                    return r
                })
                .catch((e) => {
                    return e
                })
        }
    }

    static markNotificationRead = (
        notification: Notification
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax
                .get(
                    '/user/markNotificationRead' +
                        '?notification_id=' +
                        notification.id,
                    {}
                )
                .then((r) => {
                    dispatch(pushNotification(r.notification))
                    return r
                })
        }
    }

    static markAllNotificationsRead = (): ThunkAction<
        Promise<Response>,
        any
    > => {
        return (dispatch, getState): Promise<any> => {
            return ajax.get('/user/markAllNotificationsRead', {}).then((r) => {
                dispatch(setNotifications(r.notifications))
                return r
            })
        }
    }

    static fetchNotifications = (): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax.get('/user/notifications', {}).then((r) => {
                dispatch(setNotifications(r.notifications))
                return r
            })
        }
    }

    static sendUserNotification = (
        driver_id: number,
        sender_id: number,
        message: string
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax
                .post('/user/sendUserNotification', {
                    driver_id,
                    sender_id,
                    message,
                })
                .then((r) => {
                    return r
                })
        }
    }

    static fetchMe = (): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax.get('/user/me', {}).then((r) => {
                dispatch(setProfile(r))
                return r
            })
        }
    }

    static fetchProfileTickets = (): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax.get('/user/tickets', {}).then((r) => {
                dispatch(setTickets(r.tickets))
                return r
            })
        }
    }

    static fetchResult = (
        guid: string
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax.get('/stats/result/' + guid, {}).then((r) => {
                dispatch(setResult(r))
                return r
            })
        }
    }

    static fetchResults = (
        hall_id: number,
        track_type: string | 'inner' | 'outer' = 'inner',
        filter_timestamp: PeriodType = PeriodType.Month
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax
                .get('/stats/results', {
                    params: {
                        hall_id,
                        track_type,
                        filter_timestamp,
                    },
                })
                .then((r) => {
                    dispatch(setResults(r.results))
                    return r
                })
        }
    }

    static fetchMyStats = (
        hall_id: number
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax
                .get('/stats/my', {
                    params: {
                        hall_id: hall_id,
                    },
                })
                .then((r) => {
                    dispatch(setMyStats(r))
                    return r
                })
        }
    }
    static fetchRanking = (
        filterPeriod: string,
        filterPersonType: string,
        trackLength: string,
        hall_id: number
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax
                .get('/stats/ranking', {
                    params: {
                        hall_id: hall_id,
                        filter_timestamp: filterPeriod,
                        filter_persons_type: filterPersonType,
                        limit: 20,
                    },
                })
                .then((r) => {
                    console.log(r)
                    dispatch(setRanking(r.ranking))
                    return r
                })
        }
    }

    static updateProfilePicture = (
        formData: FormData
    ): ThunkAction<Promise<Response>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax
                .post('/user/update-profile-picture', formData, {})
                .then((r) => {
                    dispatch(SessionAction.fetchMe())
                    dispatch(SessionAction.fetchProfile())
                    return r
                })
        }
    }

    static updateProfile = (
        data
    ): ThunkAction<Promise<{ user: Driver }>, any> => {
        return (dispatch, getState): Promise<any> => {
            return ajax.post('/user/update-profile', data, {}).then((r) => {
                dispatch(SessionAction.fetchMe()).then((r) => {
                    consoleLog(r)
                })
                dispatch(SessionAction.fetchProfile()).then((r) => {
                    consoleLog(r)
                })
                return r
            })
        }
    }

    static pushNotification = (
        notification: Notification
    ): ReducerSessionPushNotificationAction => {
        return pushNotification(notification) as any
    }

    static setNotifications = (notifications: Notification[]) => {
        return setNotifications(notifications)
    }

    static setSession = (session: SessionData | null) => {
        return setSession(session)
    }

    static setProfile = (profile) => {
        return setProfile(profile)
    }

    static setTickets = (tickets) => {
        return setTickets(tickets)
    }

    static setCheckin = (driver) => {
        return setCheckin(driver)
    }

    static setResult = (result) => {
        return setResult(result)
    }

    static setResults = (results) => {
        return setResults(results)
    }

    static setRanking = (ranking) => {
        return setRanking(ranking)
    }

    static setMyStats = (myStats) => {
        return setMyStats(myStats)
    }

    static setLanguage = (language) => {
        i18next.changeLanguage(language.code).then((r) => {
            // console.log(language);
        })
        return setLanguage(language)
    }

    static setThemeMode = (mode: 'light' | 'dark') => {
        return setThemeMode(mode)
    }

    static resetAppState = () => {
        return resetSession()
    }
}

export default SessionAction
